/**
 * Delivery endpoints, transport only.
 *
 * Plan D2: `destinations` and `group_destinations` are the same table twice —
 * 21 columns exist on both. Only 9 are genuinely destination-level, and those
 * are what this screen shows. Price, caps, filters, schedule, dedupe and
 * suppression are terms of *this group selling to that destination*, so they
 * live on the link and are edited in campaign detail.
 *
 * Showing the split rather than every column is the cheapest way to make the
 * D2 decision obvious before anything is migrated.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Badge, Breadcrumbs, Button, Card, Group, Loader, Modal, Select, Stack, Table, Text, TextInput,
  Title, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconPlus } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateDestination, useDestinations, useEntities } from '../api/queries';
import { notifications } from '@mantine/notifications';
import { useVocab } from '../vocab';

export function DestinationsPage() {
  const v = useVocab();
  const { data, isPending, error } = useDestinations();
  const navigate = useNavigate();
  const create = useCreateDestination();
  const buyers = useEntities('buyers');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBuyer, setNewBuyer] = useState<string | null>(null);

  /*
    A new destination asks for its name and buyer, then opens on its own page
    for everything else. It used to create an empty draft straight away, but a
    destination has to belong to a buyer, and the server refuses one without:
    the button did nothing but show "buyer_id is required".
  */
  async function addDestination() {
    if (!newName.trim() || !newBuyer) return;
    try {
      const r = await create.mutateAsync({
        name: newName.trim(),
        buyer_id: newBuyer,
        delivery_method: 'direct',
        method: 'POST',
      });
      setAdding(false);
      setNewName('');
      setNewBuyer(null);
      navigate(`/destinations/${r.destination_id}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not create', message: (e as Error).message });
    }
  }
  const unreachable = data?.destinations.filter((d) => d.link_count === 0) ?? [];

  return (
    <Stack>
      {/* Reached from Routes, the menu item. */}
      <Breadcrumbs>
        <Anchor component={Link} to="/routes" size="sm">{v.Many('route')}</Anchor>
        <Text size="sm" c="dimmed">{v.Many('destination')}</Text>
      </Breadcrumbs>
      <Group justify="space-between">
        <Title order={2}>{v.Many('destination')}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
          {`New ${v.one('destination')}`}
        </Button>
      </Group>

      <Modal opened={adding} onClose={() => setAdding(false)} title={`New ${v.one('destination')}`}>
        <Stack>
          <TextInput
            label="Name"
            placeholder="e.g. ESB live endpoint"
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
            data-autofocus
          />
          <Select
            label={v.One('buyer')}
            description={`Who receives the ${v.many('lead')} sent here`}
            placeholder={`Choose ${v.a('buyer')}`}
            searchable
            data={(buyers.data?.entities ?? []).map((b) => ({ value: b.id, label: b.name }))}
            value={newBuyer}
            onChange={setNewBuyer}
          />
          <Text size="xs" c="dimmed">
            The address, method and timeout are set on the next page.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              loading={create.isPending}
              disabled={!newName.trim() || !newBuyer}
              onClick={addDestination}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Alert variant="light" color="gray" icon={<IconInfoCircle size={18} />}>
        <Text size="sm">
          {`Where to send, and nothing else. Price, caps, filters and schedule are terms of ${v.a('campaign')}'s ${v.one('routeGroup')} sending to ${v.a('destination')}, not part of the ${v.p.urlLower}, so they are set on each ${v.one('campaign')}'s routing. `}
          <Anchor component={Link} to="/routes" size="sm">{`See every ${v.one('route')} and its terms`}</Anchor>.
        </Text>
      </Alert>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      {unreachable.length > 0 && (
        <Alert color="chartAmber" variant="light" title="Configured but unreachable">
          <Text size="sm">
            {`${unreachable.length} ${v.word(unreachable.length, 'destination')} ${unreachable.length === 1 ? 'is' : 'are'} not linked to any ${v.one('routeGroup')}, so nothing can ever be delivered to ${unreachable.length === 1 ? 'it' : 'them'}: ${unreachable.map((d) => d.name).join(', ')}.`}
          </Text>
        </Alert>
      )}

      <Card p={0}>
        {isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={860}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>{v.One('buyer')}</Table.Th>
                  <Table.Th>{v.p.url}</Table.Th>
                  <Table.Th>Method</Table.Th>
                  <Table.Th ta="right">Timeout</Table.Th>
                  <Table.Th ta="right">Used by</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.destinations.length === 0 && (
                  <Table.Tr><Table.Td colSpan={7}>
                    <Text ta="center" c="dimmed" py="lg">{`No ${v.many('destination')} configured.`}</Text>
                  </Table.Td></Table.Tr>
                )}
                {data?.destinations.map((d) => (
                  <Table.Tr
                    key={d.id}
                    onClick={() => navigate(`/destinations/${d.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Td><Text size="sm" fw={500}>{d.name || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{d.buyer_name || d.buyer_id || '—'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed" lineClamp={1} maw={280}>
                        {d.endpoint_url || d.post_endpoint || 'not set'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline" color="gray">
                        {d.delivery_method || d.method}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c="dimmed">{d.timeout_ms ? `${d.timeout_ms}ms` : '—'}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Group gap={4} justify="flex-end">
                        <Text size="sm">{d.link_count}</Text>
                        {d.link_count === 0 && (
                          <Tooltip label={`Not linked to any ${v.one('routeGroup')}`}>
                            <IconAlertTriangle size={15} color="var(--mantine-color-chartAmber-6)" />
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={d.active ? 'chartGreen' : 'gray'}>
                        {d.active ? 'Active' : 'Paused'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
